import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  TrashIcon,
  DocumentPlusIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { larBuilderApi } from '../lib/api';
import { StatusBadge } from '../components/ui';

const STATUS_VARIANTS: Record<string, 'draft' | 'info' | 'warning' | 'success' | 'neutral'> = {
  DRAFT: 'draft',
  MAPPING: 'info',
  REVIEWING: 'warning',
  VALIDATED: 'success',
  EXPORTED: 'success',
  FILED: 'neutral',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  MAPPING: 'Mapping',
  REVIEWING: 'Reviewing',
  VALIDATED: 'Validated',
  EXPORTED: 'Exported',
  FILED: 'Filed',
};

export default function LarBuilderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newYear, setNewYear] = useState(new Date().getFullYear());

  const { data, isLoading } = useQuery({
    queryKey: ['lar-workspaces'],
    queryFn: async () => {
      const res = await larBuilderApi.getWorkspaces();
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; filingYear: number }) =>
      larBuilderApi.createWorkspace(data),
    onSuccess: (res) => {
      toast.success('Workspace created');
      queryClient.invalidateQueries({ queryKey: ['lar-workspaces'] });
      setShowCreate(false);
      setNewName('');
      navigate(`/hmda/lar-builder/${res.data.id}`);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to create workspace');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => larBuilderApi.deleteWorkspace(id),
    onSuccess: () => {
      toast.success('Workspace deleted');
      queryClient.invalidateQueries({ queryKey: ['lar-workspaces'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to delete');
    },
  });

  const workspaces = data?.workspaces || [];

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    createMutation.mutate({ name: newName.trim(), filingYear: newYear });
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Delete this workspace? All records and source files will be permanently removed.')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">LAR Builder</h1>
          <p className="text-sm text-gray-600 mt-1">
            Build submission-ready HMDA LAR files from your core and LOS exports
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-ocean-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-ocean-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New Workspace
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">New LAR Workspace</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workspace Name
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g., 2025 Annual Filing"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filing Year
                  </label>
                  <select
                    value={newYear}
                    onChange={(e) => setNewYear(parseInt(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500"
                  >
                    {[2026, 2025, 2024, 2023].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setNewName(''); }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newName.trim() || createMutation.isPending}
                  className="rounded-lg bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Workspace'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12 text-gray-500">Loading workspaces...</div>
      )}

      {/* Empty state */}
      {!isLoading && workspaces.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <DocumentPlusIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Build your first LAR</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
            Upload exports from your core banking system (Jack Henry, Fiserv, etc.) and loan
            origination system (LaserPro, Encompass, etc.) to automatically map, merge, and
            assemble a submission-ready HMDA LAR file.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-ocean-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-ocean-700"
          >
            <PlusIcon className="h-4 w-4" />
            New Workspace
          </button>
        </div>
      )}

      {/* Workspace table */}
      {!isLoading && workspaces.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                <th className="px-6 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {workspaces.map((ws: any) => (
                <tr
                  key={ws.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/hmda/lar-builder/${ws.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{ws.name}</div>
                    {ws.createdBy?.name && (
                      <div className="text-xs text-gray-500">by {ws.createdBy.name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{ws.filingYear}</td>
                  <td className="px-6 py-4">
                    <StatusBadge variant={STATUS_VARIANTS[ws.status] || 'neutral'}>
                      {STATUS_LABELS[ws.status] || ws.status}
                    </StatusBadge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{ws.completeRecords}/{ws.totalRecords}</div>
                    {ws.gapRecords > 0 && (
                      <div className="text-xs text-amber-600">{ws.gapRecords} gaps</div>
                    )}
                    {ws.conflictRecords > 0 && (
                      <div className="text-xs text-red-600">{ws.conflictRecords} conflicts</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDistanceToNow(new Date(ws.updatedAt), { addSuffix: true })}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={(e) => handleDelete(e, ws.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete workspace"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
